import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import {
    Container,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Collapse,
    TableSortLabel
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Download as DownloadIcon } from '@mui/icons-material';
import { NewPageTitleBar } from '@/components';
import { LoadingOverlay } from '@/components/shared/LoadingOverlay.tsx';
import AppCard from '@/components/appcard/AppCard';
import { dataExportService } from '@/services';
import { downloadBlob } from '@/core/utils';
import { useNotifications } from '@/core/hooks/useNotifications';
import { useStore } from '@core/store';
import { ActiveLicenses } from '@/core/types';
import { getMenuData } from '@/core/data/menuData';
import { FCSecurePage } from '@/components/shared/navigation/securePage.ts';

interface ExportItem {
    id: string;
    url: string;
    description: string;
    fileName: string;
}

interface ExportCategory {
    id: string;
    license?: ActiveLicenses;
    exports: ExportItem[];
}

const DataExports: FCSecurePage = () => {
    const { t } = useTranslation();
    const { notifyErrorResponse } = useNotifications();
    const hasLicense = useStore((state) => state.hasLicense);
    const [loading, setLoading] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | false>(false);
    const [sortedExports, setSortedExports] = useState<Map<string, 'asc' | 'desc' | false>>(new Map());

    const today = useMemo(() => {
        return DateTime.fromISO(DateTime.local().toISODate(), { zone: 'UTC' }).toISODate();
    }, []);

    const trolleyShelterExports: ExportItem[] = useMemo(
        () => [
            {
                id: 'trolleyShelterLast4Weeks',
                url: 'statistics/shelterfilllevel/export/thirtydays',
                description: 'downloadCsv',
                fileName: 'TrolleyShelterLast4Weeks.csv'
            }
        ],
        []
    );

    const gatesExports: ExportItem[] = useMemo(
        () => [
            {
                id: 'gatesCountsLast4Weeks',
                url: `statistics/wanzlGates/export/thirtydays/${today}`,
                description: 'downloadCsv',
                fileName: 'GatesCountsLast4Weeks.csv'
            }
        ],
        [today]
    );

    const hybridLocExports: ExportItem[] = useMemo(
        () => [
            {
                id: 'dailyUsageLast4Weeks',
                url: `hybridLoc/export/usage/daily/${today}`,
                description: 'downloadCsv',
                fileName: 'HybridLocDailyUsageLast4Weeks.csv'
            },
            {
                id: 'detailedUsageLast4Weeks',
                url: `hybridLoc/export/usage/detailed/${today}`,
                description: 'downloadCsv',
                fileName: 'HybridLocDetailedUsageLast4Weeks.csv'
            },
            {
                id: 'distributionUsageLast4Weeks',
                url: `hybridLoc/export/usage/distribution/${today}`,
                description: 'downloadCsv',
                fileName: 'HybridLocDistributionUsageLast4Weeks.csv'
            },
            {
                id: 'batteryState',
                url: 'hybridLoc/export/batteryState',
                description: 'downloadCsv',
                fileName: 'HybridLocBatteryState.csv'
            }
        ],
        [today]
    );

    const zoneTrackerExports: ExportItem[] = useMemo(
        () => [
            {
                id: 'countOverviewLast4Weeks',
                url: `statistics/zonetracker/export/zoneentries/thirtydays/${DateTime.fromISO(today ?? '', {
                    zone: 'UTC'
                })
                    .endOf('day')
                    .toISO()}`,
                description: 'downloadCsv',
                fileName: 'ZoneTrackerOverviewLast4Weeks.csv'
            }
        ],
        [today]
    );

    const exportCategories: ExportCategory[] = useMemo(
        () => [
            {
                id: 'trolleyShelter',
                license: ActiveLicenses.Rfid,
                exports: trolleyShelterExports
            },
            {
                id: 'gates',
                exports: gatesExports
            },
            {
                id: 'hybridLoc',
                license: ActiveLicenses.HybridLoc,
                exports: hybridLocExports
            },
            {
                id: 'zoneTracker',
                license: ActiveLicenses.ShopAutomation,
                exports: zoneTrackerExports
            }
        ],
        [trolleyShelterExports, gatesExports, hybridLocExports, zoneTrackerExports]
    );

    const filteredCategories = useMemo(() => {
        return exportCategories.filter((category) => hasLicense(category.license || []));
    }, [exportCategories, hasLicense]);

    const sortedCategories = useMemo(() => {
        if (!sortOrder) {
            return filteredCategories;
        }
        const sorted = [...filteredCategories];
        sorted.sort((a, b) => {
            const nameA = t(a.id).toLowerCase();
            const nameB = t(b.id).toLowerCase();
            if (sortOrder === 'asc') {
                return nameA.localeCompare(nameB);
            }
            return nameB.localeCompare(nameA);
        });
        return sorted;
    }, [filteredCategories, sortOrder, t]);

    const toggleSort = () => {
        setSortOrder((prev) => {
            if (prev === false) return 'asc';
            if (prev === 'asc') return 'desc';
            return false;
        });
    };

    const toggleExportSort = (categoryId: string) => {
        setSortedExports((prev) => {
            const newMap = new Map(prev);
            const currentOrder = newMap.get(categoryId) || false;
            if (currentOrder === false) {
                newMap.set(categoryId, 'asc');
            } else if (currentOrder === 'asc') {
                newMap.set(categoryId, 'desc');
            } else {
                newMap.set(categoryId, false);
            }
            return newMap;
        });
    };

    const getSortedExports = (categoryId: string, exports: ExportItem[]) => {
        const order = sortedExports.get(categoryId) || false;
        if (!order) {
            return exports;
        }
        const sorted = [...exports];
        sorted.sort((a, b) => {
            const nameA = t(a.id).toLowerCase();
            const nameB = t(b.id).toLowerCase();
            if (order === 'asc') {
                return nameA.localeCompare(nameB);
            }
            return nameB.localeCompare(nameA);
        });
        return sorted;
    };

    const toggleCategory = (categoryId: string) => {
        setExpandedCategories((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(categoryId)) {
                newSet.delete(categoryId);
            } else {
                newSet.add(categoryId);
            }
            return newSet;
        });
    };

    const exportData = async (item: ExportItem) => {
        setLoading(true);
        try {
            const blob = (await dataExportService.exportBlobByUrl(item.url)) as Blob;
            downloadBlob(blob, item.fileName, blob.type);
        } catch (error) {
            notifyErrorResponse(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <NewPageTitleBar
                icon={<i className="zmdi zmdi-settings" />}
                title={t('dataExports')}
                menus={getMenuData()}
                iconSize="20px"
                currentRoute={{ path: '/statistics/dataexports' }}
                t={t}
            />

            <Container maxWidth={false} className="grid-list-xl pt-0" sx={{ mt: 2 }}>
                <Box className="row">
                    <Box className="col">
                        <AppCard customClasses="data-exports-content">
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ width: '1px' }} />
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortOrder !== false}
                                                    direction={sortOrder || 'asc'}
                                                    onClick={toggleSort}
                                                >
                                                    {t('name')}
                                                </TableSortLabel>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sortedCategories.map((category) => (
                                            <React.Fragment key={category.id}>
                                                <TableRow>
                                                    <TableCell>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggleCategory(category.id)}
                                                        >
                                                            <ExpandMoreIcon
                                                                sx={{
                                                                    transform: expandedCategories.has(category.id)
                                                                        ? 'rotate(180deg)'
                                                                        : 'rotate(0deg)',
                                                                    transition: 'transform 0.2s'
                                                                }}
                                                            />
                                                        </IconButton>
                                                    </TableCell>
                                                    <TableCell>{t(category.id)}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell colSpan={2} sx={{ p: 0, pl: 15, border: 0 }}>
                                                        <Collapse
                                                            in={expandedCategories.has(category.id)}
                                                            timeout="auto"
                                                            unmountOnExit
                                                        >
                                                            <Table size="small">
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell>
                                                                            <TableSortLabel
                                                                                active={
                                                                                    sortedExports.get(category.id) !==
                                                                                        false &&
                                                                                    sortedExports.get(category.id) !==
                                                                                        undefined
                                                                                }
                                                                                direction={
                                                                                    sortedExports.get(category.id) ||
                                                                                    'asc'
                                                                                }
                                                                                onClick={() =>
                                                                                    toggleExportSort(category.id)
                                                                                }
                                                                            >
                                                                                {t('name')}
                                                                            </TableSortLabel>
                                                                        </TableCell>
                                                                        <TableCell sx={{ width: '50px' }}>
                                                                            {t('actions')}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {getSortedExports(
                                                                        category.id,
                                                                        category.exports
                                                                    ).map((exportItem) => (
                                                                        <TableRow key={exportItem.id}>
                                                                            <TableCell>{t(exportItem.id)}</TableCell>
                                                                            <TableCell>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    title={t(exportItem.description)}
                                                                                    onClick={() =>
                                                                                        exportData(exportItem)
                                                                                    }
                                                                                >
                                                                                    <DownloadIcon />
                                                                                </IconButton>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AppCard>
                    </Box>
                </Box>
            </Container>
            <LoadingOverlay open={loading} />
        </Box>
    );
};

export default DataExports;
